/**
 * Resume Prompt screen (02) — per PRD V1 §5.2.
 *
 * Shown on app entry when an active session exists in localStorage. Two CTAs:
 * Resume (continues the existing session) or Discard (wipes the stored session
 * and routes to a fresh Session Config).
 */

import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { sessionManager } from '@/services/sessionManager';
import { cn } from '@/lib/utils';

export function ResumePromptScreen() {
  const navigate = useNavigate();
  const session = sessionManager.loadActiveSession();

  // Defensive: if a user lands here with no stored session, bounce back to
  // the entry flow (which will route to Session Config).
  useEffect(() => {
    if (!session) navigate('/', { replace: true });
  }, [session, navigate]);

  if (!session) return <Navigate to="/" replace />;

  const handleResume = () => {
    navigate('/play');
  };

  const handleDiscard = () => {
    sessionManager.discardActiveSession();
    navigate('/config', { replace: true });
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-app p-4 sm:p-8">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-elevated p-6 shadow-lg sm:p-8">
        <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-primary">
          Resume your in-progress session?
        </h1>

        <p className="mb-6 text-sm text-text-secondary">{session.autoName}</p>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={handleDiscard}
            className={cn(
              'rounded-md border border-border bg-surface-elevated px-4 py-3',
              'text-base font-medium text-text-secondary transition-colors',
              'hover:border-border-strong hover:text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-2',
            )}
          >
            Discard
          </button>

          <button
            type="button"
            onClick={handleResume}
            className={cn(
              'rounded-md bg-brand-primary px-4 py-3 text-base font-semibold',
              'text-text-on-brand transition-colors',
              'hover:bg-brand-primary-hover active:bg-brand-primary-active',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-2',
            )}
          >
            Resume
          </button>
        </div>
      </div>
    </main>
  );
}
